"use client";

import { bulkUpdateCases } from "@/app/actions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label, Select } from "@/components/ui/input";
import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";

export function BulkCases({
  cases,
  providers,
}: {
  cases: { id: string; code: string; providerName: string }[];
  providers: { id: string; name: string }[];
}) {
  const router = useRouter();
  const [selected, setSelected] = useState<string[]>([]);
  const groups = useMemo(() => {
    const map = new Map<string, number>();
    for (const id of selected) {
      const item = cases.find((c) => c.id === id);
      const key = item?.providerName ?? "Other";
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return [...map.entries()];
  }, [selected, cases]);

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm">
            Cases selected: <strong>{selected.length}</strong>
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setSelected(selected.length === cases.length ? [] : cases.map((c) => c.id))}
          >
            Select all
          </Button>
        </div>
        <div className="grid max-h-40 gap-2 overflow-auto rounded-lg border border-[var(--border)] p-3 scrollbar-thin">
          {cases.map((item) => (
            <label key={item.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.includes(item.id)}
                onChange={(e) => {
                  setSelected((cur) =>
                    e.target.checked ? [...cur, item.id] : cur.filter((id) => id !== item.id),
                  );
                }}
              />
              <span className="font-mono">{item.code}</span>
              <span className="text-[var(--text-muted)]">{item.providerName}</span>
            </label>
          ))}
        </div>
        {groups.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--text-muted)]">
                <th className="py-1">Provider</th>
                <th>Cases</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([name, count]) => (
                <tr key={name}>
                  <td className="py-1">{name}</td>
                  <td>{count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <form
          className="grid gap-3 md:grid-cols-4"
          action={async (formData) => {
            formData.set("ids", JSON.stringify(selected));
            await bulkUpdateCases(formData);
            router.refresh();
          }}
        >
          <div>
            <Label>Shared tag</Label>
            <Input name="tag" placeholder="copy-group" />
          </div>
          <div>
            <Label>Assign provider</Label>
            <Select name="providerId">
              <option value="">Leave as is</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
          <label className="flex items-end gap-2 text-sm">
            <input type="checkbox" name="generateDrafts" />
            Create drafts
          </label>
          <div className="flex items-end">
            <Button type="submit" disabled={!selected.length}>
              Apply to selected
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
